import {
  graphExportActiveIcon,
  graphExportDisabledIcon,
  graphExportIcon,
} from "assets/images";
import GroupedBarChart from "components/Chart/groupedBarChart";
import PDFGroupedBarChart from "components/Chart/pdfChart";
import CheckboxIcon from "components/CheckBoxIcon";
import CheckedBoxIcon from "components/CheckedBoxIcon";
import {
  Box,
  Button,
  Card,
  Checkbox,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  ListItemText,
  Menu,
  MenuItem,
  OutlinedInput,
  Select,
  SelectChangeEvent,
  Typography,
} from "@mui/material";
import { SELECT_ALL_VALUE } from "utils/constants/constant";
import {
  assignColors,
  convertToCountData,
  convertToPercentageData,
  covertDataToChunksArray,
  hideLoaderForBatchApiCall,
  reformatHeaderValue,
  renderSelectedValues,
  selectAllHandler,
  showLoaderForBatchApiCall,
} from "utils/helper";
import { IEnrolmentTypeFormValues } from "utils/interfaces/graph-report";
import { IEnrolmentOptionItem } from "utils/interfaces/student-enrolment";
import React, { useEffect, useRef, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { ITerm } from "utils/interfaces/term";
import { GraphType } from "utils/enums/graph";
import {
  GetGraphDetailsListSuccessPayload,
  GetGraphDetailsPayload,
} from "store/report/types";
import {
  IChartGroups,
  IGraphStudentCount,
  IStudentLabelPair,
} from "utils/interfaces/report";
import {
  GRAPH_TYPE_CATEGORY_LABELS,
  GRAPH_TYPE_CATEGORY_TABLE_HEADING,
  GRAPH_TYPE_LABELS,
} from "utils/constants/report";
import GraphReportTable from "components/GraphReportTable";
import { toast } from "react-toastify";
import { NO_RECORD_FOUND } from "utils/constants/Messages";
import { ExportToCsv } from "export-to-csv";
import CommonMultiSelectField from "components/CommonMultiSelectField";
import JsPDF from "jspdf";
import NoRecords from "components/NoRecords";
import html2canvas from "html2canvas";

interface IGenericGraphComponentProps {
  optionList: Array<IEnrolmentOptionItem>;
  graphType: GraphType;
  termList: ITerm[];
  getGraphDetailsRequest: (payload: GetGraphDetailsPayload) => {};
}

const GenericGraphComponent: React.FC<IGenericGraphComponentProps> = (
  props
) => {
  const { getGraphDetailsRequest, graphType, optionList, termList } = props;

  const yearsOptions: Array<IEnrolmentOptionItem> | undefined = termList
    ? Array.from(new Set(termList.map((term) => term.year))) // selecting unique years
        .sort((a, b) => a - b)
        .map((year) => ({
          value: year.toString(),
          label: year.toString(),
        }))
    : [];

  const termOptions: Array<IEnrolmentOptionItem> | undefined = termList?.map(
    (term) => ({
      value: term.id.toString(),
      label: term.termName,
    })
  );

  const optionListValues = optionList.map((option) => option.value);
  const colorValueMap = assignColors(optionListValues);

  const [exportRequested, setExportRequested] = useState<boolean>(false);
  const [exportMenu, setExportMenu] = useState<null | HTMLElement>(null);
  const [chartData, setChartData] = useState<IChartGroups[]>([]);
  const [percentageChartData, setPercentageChartData] = useState<
    IChartGroups[]
  >([]);
  const [responseDetails, setResponseDetails] = useState<
    Array<IGraphStudentCount>
  >([]);
  const [isYearSelected, setIsYearSelected] = useState<boolean>(false);
  const [isInitialRender, setIsInitialRender] = useState<boolean>(true);
  const chartRefs = useRef<(HTMLDivElement | null)[]>([]);

  const registerChartRef = (el: HTMLDivElement | null, idx: number) => {
    chartRefs.current[idx] = el;
  };
  const [isCombinedChecked, setIsCombinedChecked] = useState<boolean>(false);

  const exportOpen = Boolean(exportMenu);

  const { register, watch, setValue, handleSubmit, control } =
    useForm<IEnrolmentTypeFormValues>({
      defaultValues: {
        category: optionListValues,
        termIds: [],
        years: [],
      },
    });

  const selectedCategories = useWatch({ control, name: "category" });

  const handleFieldChange = (
    event: SelectChangeEvent<string[]>,
    field: keyof IEnrolmentTypeFormValues
  ) => {
    let { value } = event.target;

    if (field === "category") {
      value = selectAllHandler(
        value as string[],
        optionList,
        selectedCategories
      );
    } else if (field === "termIds") {
      value = selectAllHandler(
        value as string[],
        termOptions,
        watch("termIds")
      );
      setValue("years", []);
    } else if (field === "years") {
      value = selectAllHandler(value as string[], yearsOptions, watch("years"));
      setValue("termIds", []);
    }

    setValue(field, value as string[]);
  };

  const setCombinedData = (graphDetailList: IGraphStudentCount[]) => {
    let graphData: IGraphStudentCount[];

    if (isCombinedChecked) {
      const combinedCounts = graphDetailList
        .flatMap((data: IGraphStudentCount) => data.groupCounts)
        .reduce<IStudentLabelPair[]>((acc, curr) => {
          const existing = acc.find((item) => item.label === curr.label);
          if (existing) {
            existing.studentCount += curr.studentCount;
          } else {
            acc.push({ ...curr });
          }

          return acc;
        }, []);

      const combinedData: IGraphStudentCount = {
        groupName: GRAPH_TYPE_LABELS[graphType],
        groupCounts: combinedCounts,
      };

      graphData = [combinedData];
    } else {
      graphData = graphDetailList;
    }

    setChartData(convertToCountData(graphData));
    setPercentageChartData(convertToPercentageData(graphData));
  };

  const onFilterChangeSubmitSuccess = (
    response: GetGraphDetailsListSuccessPayload
  ) => {
    const { graphDetailList } = response;
    setResponseDetails(graphDetailList);
    setCombinedData(graphDetailList);
    if (isInitialRender) {
      setValue(
        "termIds",
        termOptions
          .filter((x) =>
            response.graphDetailList
              .map((group) => group.groupName)
              .includes(x.label)
          )
          .map((y) => y.value)
      );
      setIsInitialRender(false);
    }
    if (watch("years").length) setIsYearSelected(true);
    else setIsYearSelected(false);
    hideLoaderForBatchApiCall();
  };

  const onFilterChangeSubmitFailure = () => {
    setResponseDetails([]);
    setChartData([]);
    setPercentageChartData([]);
    setIsInitialRender(true);
    hideLoaderForBatchApiCall();
  };

  const onFilterChangeSubmit = (data?: IEnrolmentTypeFormValues) => {
    if (getGraphDetailsRequest) {
      showLoaderForBatchApiCall();
      const payload: GetGraphDetailsPayload = {
        values: {
          years: data ? data.years.map(Number) : [],
          termIds: data ? data.termIds.map(Number) : [],
          searchBy: graphType,
        },
        successCallback: onFilterChangeSubmitSuccess,
        failureCallback: onFilterChangeSubmitFailure,
      };

      getGraphDetailsRequest(payload);
    }
  };

  const handleExportClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setExportMenu(event.currentTarget);
  };
  const handleExportClose = () => {
    setExportMenu(null);
  };

  const handleExportPDFClick = () => {
    showLoaderForBatchApiCall();
    setExportRequested(true);
  };

  const getModifiedExcelChartData = (
    excelData: IChartGroups[],
    excelPercentageData: IChartGroups[],
    rowHeader: string
  ) => {
    const headers = [
      GRAPH_TYPE_CATEGORY_TABLE_HEADING[graphType],
      ...excelData.map((g) => `${g.groupName} (%)`),
    ];

    return optionListValues
      .filter((x) => selectedCategories.includes(x))
      .map((category) => {
        const row: { [key: string]: string | number } = {};
        headers.forEach((header) => {
          if (header === rowHeader) {
            row[header] = category;
          } else {
            const group = excelData.find(
              (g) => g.groupName === reformatHeaderValue(header)
            );
            const percentageGroup = excelPercentageData.find(
              (g) => g.groupName === reformatHeaderValue(header)
            );
            const count = group?.[category] ?? 0;
            const percentage = percentageGroup?.[category] ?? 0;
            row[header] = `${count} (${percentage}%)`;
          }
        });

        return row;
      });
  };

  const handleExportExcelClick = () => {
    if (!chartData?.length || !selectedCategories?.length) {
      toast.error(NO_RECORD_FOUND);

      return;
    }
    const excelData = chartData;
    const excelPercentageData = percentageChartData;

    const modifiedData = getModifiedExcelChartData(
      excelData,
      excelPercentageData,
      GRAPH_TYPE_CATEGORY_TABLE_HEADING[graphType]
    );

    const csvFileHeaders = [
      GRAPH_TYPE_CATEGORY_TABLE_HEADING[graphType],
      ...excelData.map((g) => `${g.groupName} (%)`),
    ];

    const options = {
      fieldSeparator: ",",
      quoteStrings: '"',
      decimalSeparator: ".",
      showLabels: true,
      useTextFile: false,
      useBom: true,
      headers: csvFileHeaders,
      filename: `${GRAPH_TYPE_LABELS[graphType]}`,
    };

    const csvExporter = new ExportToCsv(options);
    csvExporter.generateCsv(modifiedData);
    handleExportClose();
  };

  const handleExportPDF = async (pngStrings: string[]) => {
    const Pdf = new JsPDF("p", "mm", "a4");
    const width = Pdf.internal.pageSize.getWidth();

    pngStrings.forEach((img, idx) => {
      if (idx !== 0) Pdf.addPage();
      Pdf.addImage(img, "PNG", 0, 0, width, 0);
    });

    Pdf.save(`${GRAPH_TYPE_LABELS[graphType]}.pdf`);
    hideLoaderForBatchApiCall();
    handleExportClose();
  };

  useEffect(() => {
    setCombinedData(responseDetails);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCombinedChecked]);

  useEffect(() => {
    onFilterChangeSubmit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const totalEnrolments = React.useMemo(() => {
    return chartData.reduce((total, group) => {
      return (
        total +
        Object.entries(group).reduce((sum, [key, value]) => {
          if (
            key !== "groupName" &&
            typeof value === "number" &&
            selectedCategories.includes(key)
          ) {
            return sum + value;
          }

          return sum;
        }, 0)
      );
    }, 0);
  }, [chartData, selectedCategories]);

  const newChunkedChartData = React.useMemo(() => {
    return covertDataToChunksArray(chartData, 5);
  }, [chartData]);

  const newChunkedPercentageData = React.useMemo(() => {
    return covertDataToChunksArray(percentageChartData, 5);
  }, [percentageChartData]);

  // when export requested capture all images of charts
  useEffect(() => {
    if (!exportRequested) return;

    const captureAllCharts = async () => {
      setExportRequested(false);

      if (!chartData.length || !percentageChartData.length) {
        toast.error(NO_RECORD_FOUND);

        return;
      }

      showLoaderForBatchApiCall();

      const images = await Promise.all(
        chartRefs.current
          .filter((ref): ref is HTMLDivElement => !!ref)
          .map(async (ref) => {
            await new Promise(requestAnimationFrame);
            const canvas = await html2canvas(ref, { scale: 2 });

            return canvas.toDataURL("image/png");
          })
      );

      await handleExportPDF(images);
    };

    captureAllCharts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exportRequested]);

  return (
    <Card id={`${graphType}-graph-card`}>
      <Box className="table-card-header m-table-header">
        <Box
          sx={{
            width: "100%",
            alignItems: "center",
            flexWrap: "wrap",
            rowGap: "16px",
          }}
        >
          <Typography variant="h3">{GRAPH_TYPE_LABELS[graphType]}</Typography>
          <Box
            className="m-table-header-actions"
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: "16px",
            }}
          >
            <Box className="check-bg-box">
              <FormControl>
                <FormControlLabel
                  control={
                    <Checkbox
                      value={isCombinedChecked}
                      checked={isCombinedChecked}
                      onChange={(e) => setIsCombinedChecked(e.target.checked)}
                      icon={<CheckboxIcon />}
                      checkedIcon={<CheckedBoxIcon />}
                    />
                  }
                  label="Combine"
                />
              </FormControl>
            </Box>
            <FormControl fullWidth className="table-select select">
              <InputLabel id="enrolmentType-select-label">
                {GRAPH_TYPE_CATEGORY_LABELS[graphType]}
              </InputLabel>
              <Select
                labelId="enrolmentType-select-label"
                id="enrolmentType-select"
                multiple
                label={GRAPH_TYPE_CATEGORY_LABELS[graphType]}
                {...register("category")}
                value={selectedCategories || []}
                onChange={(e) => handleFieldChange(e, "category")}
                input={
                  <OutlinedInput
                    label={GRAPH_TYPE_CATEGORY_LABELS[graphType]}
                  />
                }
                renderValue={(selected) =>
                  selected.includes(SELECT_ALL_VALUE)
                    ? "All"
                    : renderSelectedValues(selected, optionList)
                }
              >
                <MenuItem key={SELECT_ALL_VALUE} value={SELECT_ALL_VALUE}>
                  <Checkbox
                    icon={<CheckboxIcon />}
                    checkedIcon={<CheckedBoxIcon />}
                    checked={selectedCategories?.length === optionList.length}
                  />
                  <ListItemText primary="Select All" />
                </MenuItem>
                {optionList?.map((type) => (
                  <MenuItem key={type.value} value={type.value}>
                    <Checkbox
                      checked={selectedCategories.includes(type.value)}
                      icon={<CheckboxIcon />}
                      checkedIcon={<CheckedBoxIcon />}
                    />
                    <ListItemText primary={type.label} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <CommonMultiSelectField<IEnrolmentTypeFormValues>
              name="termIds"
              label="Select Term"
              options={termOptions}
              control={control}
              isSelectAllSelected={
                watch("termIds")?.length === termOptions.length
              }
              onChange={(field) => {
                handleFieldChange(field, "termIds");
                handleSubmit(onFilterChangeSubmit)();
              }}
              renderSelectedValues={renderSelectedValues}
            />

            <CommonMultiSelectField<IEnrolmentTypeFormValues>
              name="years"
              label="Select Year"
              control={control}
              options={yearsOptions}
              isSelectAllSelected={
                watch("years")?.length === yearsOptions.length
              }
              onChange={(field) => {
                handleFieldChange(field, "years");
                handleSubmit(onFilterChangeSubmit)();
              }}
              renderSelectedValues={renderSelectedValues}
            />
            <Button
              id="export-btn"
              aria-controls={exportOpen ? "export-menu" : undefined}
              aria-haspopup="true"
              aria-expanded={exportOpen && "true"}
              onClick={handleExportClick}
              disableElevation
              disabled={
                !selectedCategories.length ||
                !percentageChartData.length ||
                !chartData.length
              }
              variant="outlined"
              sx={{ minHeight: "48px" }}
              className="btn-graph-export"
            >
              {!selectedCategories.length ||
              !percentageChartData.length ||
              !chartData.length ? (
                <img
                  src={graphExportDisabledIcon}
                  alt="list"
                  className="default"
                />
              ) : (
                <>
                  <img src={graphExportIcon} alt="list" className="default" />
                  <img
                    src={graphExportActiveIcon}
                    alt="list"
                    className="active"
                  />
                </>
              )}
            </Button>
            <Menu
              id="export-menu"
              anchorEl={exportMenu}
              open={exportOpen}
              onClose={handleExportClose}
              MenuListProps={{
                "aria-labelledby": "export-button",
              }}
              className="export-menu"
            >
              <MenuItem title="Excel" onClick={handleExportExcelClick}>
                <Button>Excel</Button>
              </MenuItem>
              <MenuItem title="PDF" onClick={handleExportPDFClick}>
                <Button>PDF</Button>
              </MenuItem>
            </Menu>
          </Box>
        </Box>
      </Box>
      <Grid item xs={12}>
        {!!percentageChartData.length &&
          !!selectedCategories.length &&
          (!!watch("termIds").length || !!watch("years").length) && (
            <GroupedBarChart
              categories={optionListValues.filter((x) =>
                selectedCategories.includes(x)
              )}
              data={percentageChartData}
              graphType={graphType}
              isYearSelected={isYearSelected}
              colorValueMap={Object.fromEntries(
                selectedCategories
                  .filter((cat) => cat in colorValueMap)
                  .map((cat) => [cat, colorValueMap[cat]])
              )}
              totalEnrolments={totalEnrolments}
            />
          )}
      </Grid>
      <Grid item xs={12}>
        {!!chartData.length &&
          !!selectedCategories.length &&
          (!!watch("termIds").length || !!watch("years").length) && (
            <GraphReportTable
              data={chartData}
              percentageData={percentageChartData}
              graphType={graphType}
              optionList={optionListValues.filter((x) =>
                selectedCategories.includes(x)
              )}
              colorValueMap={colorValueMap}
            />
          )}
      </Grid>
      <Grid item xs={12}>
        {(!responseDetails.length ||
          !chartData.length ||
          !selectedCategories.length ||
          (!watch("termIds").length && !watch("years").length)) && (
          <NoRecords />
        )}
      </Grid>
      {/* COMPONENT RELATED TO DOWNLOAD IMAGE OF BARCHART  */}
      <Grid item xs={12}>
        {percentageChartData &&
          chartData &&
          !!selectedCategories.length &&
          newChunkedChartData &&
          newChunkedChartData.map((chartChunk, idx) => (
            <Box
              key={`${chartChunk}-box-pdf`}
              style={{
                position: "absolute",
                left: -9999,
                top: 0,
              }}
            >
              <PDFGroupedBarChart
                percentageData={newChunkedPercentageData[idx] || []}
                chartData={chartChunk}
                categories={optionListValues.filter((x) =>
                  selectedCategories.includes(x)
                )}
                graphType={graphType}
                isYearSelected={isYearSelected}
                colorValueMap={Object.fromEntries(
                  selectedCategories
                    .filter((cat) => cat in colorValueMap)
                    .map((cat) => [cat, colorValueMap[cat]])
                )}
                totalEnrolments={totalEnrolments}
                registerRef={registerChartRef}
                index={idx}
              />
            </Box>
          ))}
      </Grid>
    </Card>
  );
};

export default GenericGraphComponent;
